/**
 * @fileoverview Data Gap Analysis Dashboard
 * Visualizes procurement data coverage, gaps, and scrape recommendations.
 * @module DataGapAnalysis
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Database, RefreshCw, AlertCircle, AlertTriangle,
  CheckCircle2, Clock, TrendingUp, BarChart3,
  ChevronDown, ChevronUp, ExternalLink, Calendar,
  Copy, Filter, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Components
import CoverageTimeline from './CoverageTimeline';
import ScrapeRecommendations from './ScrapeRecommendations';
import DuplicateOverview from './DuplicateOverview';
import GapDetailModal from './GapDetailModal';

// Hook
import useGapAnalysisData from '../../hooks/useGapAnalysisData';

// Utilities
import logger from '../../lib/logger';

// Styles
import './DataGapAnalysis.css';

/**
 * Format currency value
 */
const formatCurrency = (value) => {
  if (value == null) return '$0';
  const num = parseFloat(value);
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
};

/**
 * Format number with commas
 */
const formatNumber = (value) => {
  if (value == null) return '0';
  return parseInt(value, 10).toLocaleString();
};

/**
 * Get freshness status color class
 */
const getFreshnessClass = (status) => {
  switch (status) {
    case 'current': return 'dga-status-current';
    case 'aging': return 'dga-status-aging';
    case 'stale': return 'dga-status-stale';
    case 'very_stale': return 'dga-status-very-stale';
    case 'no_data': return 'dga-status-no-data';
    default: return '';
  }
};

/**
 * Get severity color class
 */
const getGapCategoryLabel = (cat) => {
  if (cat === 'surveyed_gap') return { label: '🔍 Surveyed', cls: 'dga-gap-cat-surveyed_gap' };
  if (cat === 'partial') return { label: '⚠️ Partial', cls: 'dga-gap-cat-partial' };
  return { label: '❓ Unknown', cls: 'dga-gap-cat-never_checked' };
};

const getSeverityClass = (severity) => {
  switch (severity) {
    case 'critical': return 'dga-severity-critical';
    case 'high': return 'dga-severity-high';
    case 'medium': return 'dga-severity-medium';
    case 'low': return 'dga-severity-low';
    default: return '';
  }
};

/**
 * Loading skeleton component
 */
const LoadingSkeleton = () => (
  <div className="dga-dashboard">
    <div className="dga-header">
      <div className="dga-skeleton" style={{ width: 200, height: 32 }} />
    </div>
    <div className="dga-stats-grid">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="dga-skeleton dga-skeleton-stat" />
      ))}
    </div>
    <div className="dga-skeleton" style={{ width: '100%', height: 300, marginTop: 24 }} />
  </div>
);

/**
 * Error display component
 */
const ErrorDisplay = ({ error, onRetry }) => (
  <div className="dga-error">
    <AlertCircle size={48} />
    <span>Error loading gap analysis data: {error}</span>
    <button className="dga-btn dga-btn-primary" onClick={onRetry}>
      Retry
    </button>
  </div>
);

/**
 * Stats Card Component
 */
const StatsCard = ({ icon: Icon, label, value, subValue, trend, className }) => (
  <div className={`dga-stat-card ${className || ''}`}>
    <div className="dga-stat-icon">
      <Icon size={20} />
    </div>
    <div className="dga-stat-content">
      <span className="dga-stat-value">{value}</span>
      <span className="dga-stat-label">{label}</span>
      {subValue && <span className="dga-stat-sub">{subValue}</span>}
    </div>
    {trend && (
      <div className={`dga-stat-trend ${trend > 0 ? 'positive' : 'negative'}`}>
        <TrendingUp size={14} />
        <span>{Math.abs(trend)}%</span>
      </div>
    )}
  </div>
);

/**
 * Coverage Table Row Component
 */
const CoverageRow = ({ dept, onViewDetails, onNavigateToScraper }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={`dga-coverage-row ${getFreshnessClass(dept.freshness_status)}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="dga-dept-cell">
          <div className="dga-dept-info">
            <span className="dga-dept-acronym">{dept.acronym || '—'}</span>
            <span className="dga-dept-name">{dept.common_name || dept.official_name}</span>
          </div>
        </td>
        <td className="dga-coverage-cell">
          <div className="dga-coverage-bar-wrapper">
            <div
              className="dga-coverage-bar"
              style={{ width: `${Math.min(dept.coverage_percentage || 0, 100)}%` }}
            />
            <span className="dga-coverage-pct">{dept.coverage_percentage || 0}%</span>
          </div>
        </td>
        <td className="dga-records-cell">{formatNumber(dept.total_records)}</td>
        <td className="dga-spend-cell">{formatCurrency(dept.total_spend)}</td>
        <td className="dga-date-cell">
          {dept.latest_data
            ? new Date(dept.latest_data).toLocaleDateString()
            : '—'}
        </td>
        <td className="dga-status-cell">
          <span className={`dga-status-badge ${getFreshnessClass(dept.freshness_status)}`}>
            {dept.freshness_status?.replace('_', ' ') || 'unknown'}
          </span>
        </td>
        <td className="dga-actions-cell">
          <button
            className="dga-btn-icon"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="dga-expanded-row">
          <td colSpan={7}>
            <div className="dga-expanded-content">
              <div className="dga-expanded-stats">
                <div className="dga-expanded-stat">
                  <span className="dga-expanded-label">Date Range</span>
                  <span className="dga-expanded-value">
                    {dept.earliest_data
                      ? `${new Date(dept.earliest_data).toLocaleDateString()} - ${new Date(dept.latest_data).toLocaleDateString()}`
                      : 'No data'}
                  </span>
                </div>
                <div className="dga-expanded-stat">
                  <span className="dga-expanded-label">Months with Data</span>
                  <span className="dga-expanded-value">{dept.months_with_data || 0}</span>
                </div>
                <div className="dga-expanded-stat">
                  <span className="dga-expanded-label">Duplicates</span>
                  <span className="dga-expanded-value">
                    {formatNumber(dept.duplicate_count)} ({((dept.duplicate_count / dept.total_records) * 100 || 0).toFixed(1)}%)
                  </span>
                </div>
                <div className="dga-expanded-stat">
                  <span className="dga-expanded-label">Days Since Update</span>
                  <span className="dga-expanded-value">{dept.days_since_last_data || '—'}</span>
                </div>
              </div>
              <div className="dga-expanded-actions">
                <button
                  className="dga-btn dga-btn-secondary"
                  onClick={(e) => { e.stopPropagation(); onViewDetails(dept); }}
                >
                  View Details
                </button>
                {dept.freshness_status !== 'current' && (
                  <button
                    className="dga-btn dga-btn-primary"
                    onClick={(e) => { e.stopPropagation(); onNavigateToScraper(dept); }}
                  >
                    <RefreshCw size={14} />
                    Scrape Now
                  </button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * Main Data Gap Analysis Dashboard Component
 */
const DataGapAnalysis = ({ selectedDepartment: selectedDepartmentProp }) => {
  const navigate = useNavigate();

  // State
  const [activeView, setActiveView] = useState('timeline');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [freshnessFilter, setFreshnessFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'freshness_status', direction: 'asc' });

  // Filter by selected department when one is chosen from TopNav
  // Memoize to prevent infinite re-fetch loops (new array !== old array)
  const departmentIds = useMemo(
    () => selectedDepartmentProp?.department_id
      ? [selectedDepartmentProp.department_id]
      : undefined,
    [selectedDepartmentProp?.department_id]
  );

  // Fetch data — scoped to selected department if any
  const {
    coverage,
    coverageStats,
    gaps,
    gapStats,
    suggestions,
    duplicates,
    timeline,
    loading,
    error,
    refetch,
    refreshData
  } = useGapAnalysisData({ departmentIds });

  // Compute department-specific stats from coverage data when a dept is selected
  const deptStats = useMemo(() => {
    if (!selectedDepartmentProp || !coverage || coverage.length === 0) return null;
    // coverage is already filtered by departmentId from the hook
    const dept = coverage[0]; // Single department
    if (!dept) return null;
    return {
      department_name: dept.common_name || dept.official_name || dept.acronym,
      total_records: parseInt(dept.total_records) || 0,
      total_spend: parseFloat(dept.total_spend) || 0,
      coverage_percentage: parseFloat(dept.coverage_percentage) || 0,
      days_since_last_data: parseInt(dept.days_since_last_data) || 0,
      freshness_status: dept.freshness_status || 'no_data',
      duplicate_count: parseInt(dept.duplicate_count) || 0,
      months_with_data: parseInt(dept.months_with_data) || 0,
      earliest_data: dept.earliest_data,
      latest_data: dept.latest_data,
    };
  }, [selectedDepartmentProp, coverage]);

  // Log component mount
  useEffect(() => {
    logger.component.info('DataGapAnalysis', 'mounted');
    return () => {
      logger.component.info('DataGapAnalysis', 'unmounted');
    };
  }, []);

  // Filter and sort coverage data
  const filteredCoverage = useMemo(() => {
    if (!coverage) return [];

    let filtered = [...coverage];

    // Apply freshness filter
    if (freshnessFilter !== 'all') {
      filtered = filtered.filter(d => d.freshness_status === freshnessFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle special sorting for freshness status
      if (sortConfig.key === 'freshness_status') {
        const order = { no_data: 0, very_stale: 1, stale: 2, aging: 3, current: 4 };
        aVal = order[aVal] ?? 5;
        bVal = order[bVal] ?? 5;
      }

      // Handle numeric values
      if (typeof aVal === 'number' || !isNaN(parseFloat(aVal))) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [coverage, freshnessFilter, sortConfig]);

  /**
   * Handle column header click for sorting
   */
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  /**
   * Handle view details click
   */
  const handleViewDetails = useCallback((dept) => {
    setSelectedDepartment(dept);
    setDetailModalOpen(true);
    logger.ui.info('DataGapAnalysis', 'view_details', { departmentId: dept.department_id });
  }, []);

  /**
   * Navigate to scraper with pre-filled department
   */
  const handleNavigateToScraper = useCallback((dept) => {
    navigate(`/scraper?department=${dept.department_id}`);
    logger.ui.info('DataGapAnalysis', 'navigate_scraper', { departmentId: dept.department_id });
  }, [navigate]);

  /**
   * Handle refresh button click
   */
  const handleRefresh = useCallback(async () => {
    try {
      await refreshData();
      logger.ui.info('DataGapAnalysis', 'data_refreshed');
    } catch (err) {
      logger.api.error('DataGapAnalysis:refresh_failed', null, err);
    }
  }, [refreshData]);

  // Show loading state
  if (loading && !coverage) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error && !coverage) {
    return <ErrorDisplay error={error.message || error} onRetry={refetch} />;
  }

  return (
    <div className="dga-dashboard">
      {/* Header */}
      <div className="dga-header">
        <div className="dga-header-left">
          <Database size={28} />
          <div>
            <h1>{deptStats ? `${deptStats.department_name} — Data Coverage` : 'Data Coverage Analysis'}</h1>
            <p>{deptStats
              ? `${deptStats.earliest_data ? new Date(deptStats.earliest_data).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '?'} to ${deptStats.latest_data ? new Date(deptStats.latest_data).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '?'}`
              : 'Select a department to view coverage details'
            }</p>
          </div>
        </div>
        <div className="dga-header-actions">
          <button
            className="dga-btn dga-btn-ghost"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data"
          >
            <RefreshCw size={18} className={loading ? 'dga-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stats Grid — department-specific when dept is selected */}
      <div className="dga-stats-grid">
        {deptStats ? (
          <>
            <StatsCard
              icon={BarChart3}
              label="Records"
              value={formatNumber(deptStats.total_records)}
              subValue={`${deptStats.months_with_data} months with data`}
            />
            <StatsCard
              icon={TrendingUp}
              label="Total Spend"
              value={formatCurrency(deptStats.total_spend)}
            />
            <StatsCard
              icon={Database}
              label="Coverage"
              value={`${deptStats.coverage_percentage}%`}
              subValue={deptStats.freshness_status?.replace('_', ' ')}
            />
            <StatsCard
              icon={Clock}
              label="Days Since Data"
              value={Math.max(0, deptStats.days_since_last_data)}
              subValue="days"
              className={deptStats.days_since_last_data > 90 ? 'dga-stat-warning' : ''}
            />
            <StatsCard
              icon={AlertTriangle}
              label="Gaps Detected"
              value={formatNumber(gapStats?.total_unresolved)}
              subValue={`${formatNumber(gapStats?.critical_count)} critical`}
              className={gapStats?.critical_count > 0 ? 'dga-stat-warning' : ''}
            />
            <StatsCard
              icon={Copy}
              label="Duplicates"
              value={formatNumber(deptStats.duplicate_count)}
              subValue={`${((deptStats.duplicate_count / deptStats.total_records) * 100 || 0).toFixed(1)}%`}
            />
          </>
        ) : (
          <>
            <StatsCard
              icon={Database}
              label="Departments"
              value={formatNumber(coverageStats?.total_departments)}
              subValue={`${formatNumber(coverageStats?.current_count)} current`}
            />
            <StatsCard
              icon={BarChart3}
              label="Total Records"
              value={formatNumber(coverageStats?.total_records)}
            />
            <StatsCard
              icon={TrendingUp}
              label="Total Spend"
              value={formatCurrency(coverageStats?.total_spend)}
            />
            <StatsCard
              icon={AlertTriangle}
              label="Gaps Detected"
              value={formatNumber(gapStats?.total_unresolved)}
              subValue={`${formatNumber(gapStats?.critical_count)} critical`}
            />
            <StatsCard
              icon={Clock}
              label="Avg Days Since Data"
              value={Math.round(Math.max(0, coverageStats?.avg_days_since_data || 0))}
              subValue="days"
            />
            <StatsCard
              icon={Copy}
              label="Duplicates"
              value={formatNumber(coverageStats?.total_duplicates)}
              subValue={`${((coverageStats?.total_duplicates / coverageStats?.total_records) * 100 || 0).toFixed(1)}%`}
            />
          </>
        )}
      </div>

      {/* View Tabs */}
      <div className="dga-tabs">
        <button
          className={`dga-tab ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          Overview
        </button>
        <button
          className={`dga-tab ${activeView === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveView('timeline')}
        >
          Timeline
        </button>
        <button
          className={`dga-tab ${activeView === 'gaps' ? 'active' : ''}`}
          onClick={() => setActiveView('gaps')}
        >
          Gaps
          {gapStats?.total_unresolved > 0 && (
            <span className="dga-tab-badge">{gapStats.total_unresolved}</span>
          )}
        </button>
        <button
          className={`dga-tab ${activeView === 'recommendations' ? 'active' : ''}`}
          onClick={() => setActiveView('recommendations')}
        >
          Recommendations
        </button>
      </div>

      {/* Overview View */}
      {activeView === 'overview' && (
        <div className="dga-overview">
          {/* Freshness Status Summary */}
          <div className="dga-freshness-summary">
            <h3>Coverage Status</h3>
            <div className="dga-freshness-bars">
              <div
                className="dga-freshness-segment dga-status-current"
                style={{ flex: coverageStats?.current_count || 0 }}
                title={`Current: ${coverageStats?.current_count || 0}`}
              />
              <div
                className="dga-freshness-segment dga-status-aging"
                style={{ flex: coverageStats?.aging_count || 0 }}
                title={`Aging: ${coverageStats?.aging_count || 0}`}
              />
              <div
                className="dga-freshness-segment dga-status-stale"
                style={{ flex: coverageStats?.stale_count || 0 }}
                title={`Stale: ${coverageStats?.stale_count || 0}`}
              />
              <div
                className="dga-freshness-segment dga-status-very-stale"
                style={{ flex: coverageStats?.very_stale_count || 0 }}
                title={`Very Stale: ${coverageStats?.very_stale_count || 0}`}
              />
              <div
                className="dga-freshness-segment dga-status-no-data"
                style={{ flex: coverageStats?.no_data_count || 0 }}
                title={`No Data: ${coverageStats?.no_data_count || 0}`}
              />
            </div>
            <div className="dga-freshness-legend">
              <span className="dga-legend-item dga-status-current">
                <span className="dga-legend-dot" />
                Current ({coverageStats?.current_count || 0})
              </span>
              <span className="dga-legend-item dga-status-aging">
                <span className="dga-legend-dot" />
                Aging ({coverageStats?.aging_count || 0})
              </span>
              <span className="dga-legend-item dga-status-stale">
                <span className="dga-legend-dot" />
                Stale ({coverageStats?.stale_count || 0})
              </span>
              <span className="dga-legend-item dga-status-very-stale">
                <span className="dga-legend-dot" />
                Very Stale ({coverageStats?.very_stale_count || 0})
              </span>
              <span className="dga-legend-item dga-status-no-data">
                <span className="dga-legend-dot" />
                No Data ({coverageStats?.no_data_count || 0})
              </span>
            </div>
          </div>

          {/* Filters */}
          <div className="dga-filters">
            <div className="dga-filter-group">
              <Filter size={16} />
              <select
                value={freshnessFilter}
                onChange={(e) => setFreshnessFilter(e.target.value)}
                className="dga-filter-select"
              >
                <option value="all">All Status</option>
                <option value="current">Current</option>
                <option value="aging">Aging (60+ days)</option>
                <option value="stale">Stale (90+ days)</option>
                <option value="very_stale">Very Stale (180+ days)</option>
                <option value="no_data">No Data</option>
              </select>
            </div>
            <span className="dga-filter-count">
              {filteredCoverage.length} departments
            </span>
          </div>

          {/* Coverage Table */}
          <div className="dga-table-wrapper">
            <table className="dga-coverage-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('official_name')} className="dga-sortable">
                    Department
                    {sortConfig.key === 'official_name' && (
                      <span className="dga-sort-indicator">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('coverage_percentage')} className="dga-sortable">
                    Coverage
                    {sortConfig.key === 'coverage_percentage' && (
                      <span className="dga-sort-indicator">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('total_records')} className="dga-sortable">
                    Records
                    {sortConfig.key === 'total_records' && (
                      <span className="dga-sort-indicator">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('total_spend')} className="dga-sortable">
                    Spend
                    {sortConfig.key === 'total_spend' && (
                      <span className="dga-sort-indicator">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('latest_data')} className="dga-sortable">
                    Last Data
                    {sortConfig.key === 'latest_data' && (
                      <span className="dga-sort-indicator">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('freshness_status')} className="dga-sortable">
                    Status
                    {sortConfig.key === 'freshness_status' && (
                      <span className="dga-sort-indicator">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCoverage.map(dept => (
                  <CoverageRow
                    key={dept.department_id}
                    dept={dept}
                    onViewDetails={handleViewDetails}
                    onNavigateToScraper={handleNavigateToScraper}
                  />
                ))}
                {filteredCoverage.length === 0 && (
                  <tr>
                    <td colSpan={7} className="dga-empty">
                      No departments match the current filter
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Timeline View */}
      {activeView === 'timeline' && (
        <CoverageTimeline
          data={timeline}
          onDepartmentClick={handleViewDetails}
        />
      )}

      {/* Gaps View */}
      {activeView === 'gaps' && (
        <div className="dga-gaps-view">
          {/* Gap Stats */}
          <div className="dga-gap-stats">
            <div className={`dga-gap-stat ${getSeverityClass('critical')}`}>
              <span className="dga-gap-stat-value">{gapStats?.critical_count || 0}</span>
              <span className="dga-gap-stat-label">Critical</span>
            </div>
            <div className={`dga-gap-stat ${getSeverityClass('high')}`}>
              <span className="dga-gap-stat-value">{gapStats?.high_count || 0}</span>
              <span className="dga-gap-stat-label">High</span>
            </div>
            <div className={`dga-gap-stat ${getSeverityClass('medium')}`}>
              <span className="dga-gap-stat-value">{gapStats?.medium_count || 0}</span>
              <span className="dga-gap-stat-label">Medium</span>
            </div>
            <div className={`dga-gap-stat ${getSeverityClass('low')}`}>
              <span className="dga-gap-stat-value">{gapStats?.low_count || 0}</span>
              <span className="dga-gap-stat-label">Low</span>
            </div>
          </div>

          {/* Gaps List */}
          <div className="dga-gaps-list">
            {gaps && gaps.length > 0 ? (
              gaps.map(gap => (
                <div key={gap.gap_id} className={`dga-gap-card ${getSeverityClass(gap.severity)}`}>
                  <div className="dga-gap-header">
                    <div className="dga-gap-dept">
                      <span className="dga-gap-acronym">{gap.acronym || '—'}</span>
                      <span className="dga-gap-name">{gap.department_name}</span>
                    </div>
                    <span className={`dga-severity-badge ${getSeverityClass(gap.severity)}`}>
                      {gap.severity}
                    </span>
                  </div>
                  <div className="dga-gap-body">
                    <div className="dga-gap-dates">
                      <Calendar size={14} />
                      <span>
                        {new Date(gap.gap_start_date).toLocaleDateString()} - {new Date(gap.gap_end_date).toLocaleDateString()}
                      </span>
                      <span className="dga-gap-days">({gap.gap_days} days)</span>
                    </div>
                    <div className="dga-gap-type">
                      <span className="dga-gap-type-label">{gap.gap_type?.replace('_', ' ')}</span>
                      {gap.gap_category && gap.gap_category !== 'never_checked' && (
                        <span className={`dga-gap-category dga-gap-cat-${gap.gap_category}`}>
                          {gap.gap_category === 'surveyed_gap' ? '🔍 surveyed gap' : '⚠️ partial'}
                          {gap.completeness_pct != null && ` · ${gap.completeness_pct.toFixed(0)}%`}
                        </span>
                      )}
                      <span className="dga-gap-estimate">
                        ~{formatNumber(gap.estimated_missing_records)} records
                        {gap.census_expected != null && (
                          <span className="dga-gap-census"> ({gap.census_expected} expected/mo)</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="dga-gap-actions">
                    <button
                      className="dga-btn dga-btn-primary dga-btn-sm"
                      onClick={() => handleNavigateToScraper({ department_id: gap.department_id })}
                    >
                      <RefreshCw size={14} />
                      Fill Gap
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="dga-empty-state">
                <CheckCircle2 size={48} />
                <h3>No Gaps Detected</h3>
                <p>All departments have continuous data coverage</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recommendations View */}
      {activeView === 'recommendations' && (
        <ScrapeRecommendations
          suggestions={suggestions}
          onNavigateToScraper={handleNavigateToScraper}
        />
      )}

      {/* Duplicate Overview (always visible in sidebar) */}
      {duplicates && duplicates.length > 0 && (
        <DuplicateOverview duplicates={duplicates} />
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedDepartment && (
        <GapDetailModal
          department={selectedDepartment}
          onClose={() => setDetailModalOpen(false)}
          onNavigateToScraper={handleNavigateToScraper}
        />
      )}
    </div>
  );
};

export default DataGapAnalysis;
